// D9-09 (#408) — one canonical mapping from the Service doors' `error_code`
// vocabulary to fixed advisor-language copy, so no caller ever renders a raw
// server message for a known, expected refusal. Source of the envelope:
// apps/tenant/bootstrap/app.php's exception renderers —
// `{message, error_code, errors?: {field: string[]}, request_id}`.
// Parsers reused, never re-implemented: `getErrorCode`/`getErrorMessage`
// from `@marine/utils/getErrorMessage`.
import { isAxiosError } from 'axios';
import { getErrorCode, getErrorMessage } from '@marine/utils/getErrorMessage';
import { formatCents } from './money';

export interface ServiceErrorResult {
  readonly message: string;
  readonly refetch: boolean;
  readonly fieldErrors?: Record<string, string[]>;
}

const CHANGED_SINCE_OPENED_MESSAGE =
  'This work order changed since you opened it — refreshing.';
const NOT_EDITABLE_MESSAGE =
  'This can no longer be edited because the work order has been invoiced or closed.';

/** Reads a numeric key off the envelope's `details` object (T-19-45: only a
 * real number is ever interpolated into copy). */
function numericDetail(error: unknown, key: string): number | undefined {
  if (!isAxiosError(error)) {
    return undefined;
  }

  const details = (error.response?.data as { details?: unknown } | undefined)?.details;
  if (details && typeof details === 'object') {
    const value = (details as Record<string, unknown>)[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }

  return undefined;
}

function extractFieldErrors(error: unknown): Record<string, string[]> | undefined {
  if (!isAxiosError(error)) {
    return undefined;
  }

  const errors = (error.response?.data as { errors?: unknown } | undefined)?.errors;

  return errors && typeof errors === 'object'
    ? (errors as Record<string, string[]>)
    : undefined;
}

/**
 * Maps a Service door's error to advisor-language copy and tells the
 * caller whether the resource needs a refetch. `action` is only
 * interpolated into the 403 fallback copy — every named `error_code` has
 * its own fixed message regardless of `action`.
 */
export function serviceErrorMessage(error: unknown, action: string): ServiceErrorResult {
  const code = getErrorCode(error);

  switch (code) {
    case 'INVALID_WORK_ORDER_TRANSITION':
    case 'INVALID_JOB_TRANSITION':
    case 'INVALID_JOB_PART_TRANSITION':
    case 'INVALID_WORK_ORDER_INVOICE_TRANSITION':
    case 'INVALID_SUBCONTRACT_ASSIGNMENT_TRANSITION':
      return { message: CHANGED_SINCE_OPENED_MESSAGE, refetch: true };

    case 'WORK_ORDER_NOT_EDITABLE':
    case 'JOB_NOT_EDITABLE':
    case 'JOB_PART_NOT_EDITABLE':
      return { message: NOT_EDITABLE_MESSAGE, refetch: true };

    case 'INVOICE_EMPTY':
      return {
        message: 'Add at least one job before generating the invoice.',
        refetch: false,
      };

    case 'INVOICE_NOT_PAYABLE':
      return {
        message: "This invoice isn't open for payment.",
        refetch: true,
      };

    case 'ACCOUNTING_LOCKED':
      return {
        message: 'This record was exported to accounting and is locked. Ask finance to unlock it.',
        refetch: true,
      };

    case 'ACCOUNTING_BATCH_EMPTY':
      return {
        message: 'Nothing to export — every row is already synced.',
        refetch: true,
      };

    case 'INVOICE_NOT_GENERATED':
      return { message: 'Generate the invoice first.', refetch: true };

    case 'WORK_ORDER_ALREADY_REISSUED':
      return {
        message: 'This work order has already been reversed and reissued.',
        refetch: true,
      };

    case 'WORK_ORDER_DISPUTED':
      return {
        message:
          'This work order is disputed. Resolve the dispute before taking or refunding money.',
        refetch: true,
      };

    case 'DISPUTE_NOT_COVERED':
      return {
        message:
          "That credit note doesn't cover the disputed balance. Issue a larger credit note or resolve as accepted.",
        refetch: false,
      };

    case 'WORK_ORDER_BILLING_CLOSED':
      return {
        message:
          'This work order has been invoiced. Billing corrections go through reverse-and-reissue; rework that needs parts goes on a new internal work order.',
        refetch: true,
      };

    // D17-25 — the work order reached Closed/Cancelled/Voided under the dialog.
    case 'WORK_ORDER_TERMINAL':
      return {
        message: 'This work order is closed — signatures can no longer be captured on it.',
        refetch: true,
      };

    // D17-07 — the server's blank-image check (the pad's client check is only UX).
    case 'SIGNATURE_EMPTY':
      return {
        message: 'The signature is blank — ask the customer to sign again.',
        refetch: false,
      };

    case 'SUBCONTRACT_TERMS_FROZEN':
      return {
        message:
          "This sublet's price is locked once the customer approves it. Raise a dispute to re-quote, or cancel it and raise a new one.",
        refetch: true,
      };

    case 'SUBCONTRACT_NOT_PAID_BY_CUSTOMER':
      return {
        message: getErrorMessage(
          error,
          'The customer has not paid enough on this work order to settle this sublet yet.',
        ),
        refetch: true,
      };

    // D19-10 — a sublet-managed misc charge cannot be edited/removed directly.
    case 'SUBLET_CHARGE_MANAGED_BY_ASSIGNMENT':
      return {
        message:
          'This charge comes from a sublet. Change it through the sublet — raise a dispute to re-quote, or void it.',
        refetch: true,
      };

    // D19-05 / D19-19 — voiding a billed sublet needs a covering credit note.
    case 'SUBCONTRACT_CHARGE_NOT_CREDITED': {
      const shortfall = numericDetail(error, 'shortfall_cents');
      return {
        message:
          shortfall !== undefined
            ? `Finance must issue a credit note of at least ${formatCents(shortfall)} on this work order before this billed sublet can be voided.`
            : getErrorMessage(
                error,
                'Finance must issue a credit note for this billed sublet before it can be voided.',
              ),
        refetch: true,
      };
    }

    // Phase 19 BUSINESS_RULE_VIOLATION refusals (R-SUBLET-JOB-MISSING,
    // R-SUBLET-AMOUNT-UNKNOWN, R-SUBLET-JOB-CANCELLED, R-RATE-NOT-SETTLED)
    // deliberately have no case: the default branch shows the server message.

    // 18-REVIEW WR-03 — dispatch on a job the sublet automation cannot move
    // (neither InProgress nor already AwaitingSubletReturn).
    case 'JOB_NOT_IN_PROGRESS':
      return {
        message: 'Start the job before dispatching sublet work.',
        refetch: true,
      };

    case 'HIN_ALREADY_ON_FILE':
      // The server's own message names the record — pass it through
      // verbatim rather than inventing a generic one here.
      return {
        message: getErrorMessage(error, 'That HIN is already on file.'),
        refetch: false,
      };

    case 'VALIDATION_ERROR': {
      const fieldErrors = extractFieldErrors(error);
      return {
        message: getErrorMessage(error, 'Please fix the highlighted fields.'),
        refetch: false,
        ...(fieldErrors ? { fieldErrors } : {}),
      };
    }

    default:
      // A 403 carries its own `error_code` today (`FORBIDDEN`), so this
      // checks the HTTP status directly rather than "no error_code" —
      // status is the reliable signal regardless of what code (if any)
      // rides along with it.
      if (isAxiosError(error) && error.response?.status === 403) {
        return {
          message: `You don't have permission to ${action}. Ask your manager.`,
          refetch: false,
        };
      }

      return {
        message: getErrorMessage(error, `Failed to ${action}.`),
        refetch: false,
      };
  }
}

/**
 * Applies the first message per field from `serviceErrorMessage`'s
 * `fieldErrors` onto an RHF `setError` — the one canonical implementation
 * shared by every Service form (no existing RHF-setError-on-422 pattern
 * exists elsewhere in this repo).
 *
 * 09-UAT gap 2 — a field the server names that the caller does not render
 * (e.g. `location_id` when the Sheet's location field is hidden) used to
 * get `setError`'d anyway: an error nobody renders, which blocks the form
 * from re-submitting with no visible reason. When `knownFields` is passed,
 * `setError` is skipped for any field NOT in it and its first message is
 * returned instead, so the caller can toast it. Omitting `knownFields`
 * keeps every existing caller's behaviour byte-for-byte identical (every
 * field is set, nothing is returned).
 */
export function applyFieldErrors<TFieldName extends string>(
  setError: (name: TFieldName, error: { type: string; message: string }) => void,
  fieldErrors: Record<string, string[]>,
  knownFields?: readonly string[],
): string[] {
  const unmapped: string[] = [];

  for (const [field, messages] of Object.entries(fieldErrors)) {
    const [first] = messages;
    if (!first) continue;

    if (knownFields && !knownFields.includes(field)) {
      unmapped.push(first);
      continue;
    }

    setError(field as TFieldName, { type: 'server', message: first });
  }

  return unmapped;
}
